import { UseMutationOptions, useQueryClient } from '@tanstack/react-query';

import { DisplayConditionPostBody } from 'hooks/mutations/useCreateDisplayCondition';
import QueryKeys from 'hooks/queries/keys';
import { RowDisplayCondition } from 'hooks/queries/useDisplayConditions';
import { ApiMethods, CuredApiPaths } from 'models/enums';

import useMutation from '../useMutation';

import MutationKeys from './keys';

export type UpdateDisplayConditionBody = Partial<DisplayConditionPostBody>;

/**
 * Updates an existing display condition.
 * @param conditionId the id of the condition being edited
 * @param mutateOptions react-query useMutation option
 * See https://react-query-v3.tanstack.com/reference/useMutation for options and return value
 */
const useUpdateDisplayCondition = (
    conditionId: number,
    mutateOptions?: UseMutationOptions<RowDisplayCondition, Error, UpdateDisplayConditionBody>
) => {
    const queryClient = useQueryClient();
    const { onSuccess: onSuccessOption, ...restMutateOptions } = mutateOptions ?? {};

    return useMutation<UpdateDisplayConditionBody, RowDisplayCondition>(
        MutationKeys.UPDATE_DISPLAY_CONDITION(conditionId),
        `${CuredApiPaths.ROW_DISPLAY_CONDITIONS}/${conditionId}`,
        {
            onSuccess: async (data, variables, context) => {
                // Await so the Manage table's cache is already fresh by the time this
                // resolves — otherwise closing the dialog and reopening Edit immediately can
                // race the background refetch and show the pre-update row.
                await Promise.all([
                    queryClient.invalidateQueries(QueryKeys.ROW_DISPLAY_CONDITIONS),
                    queryClient.invalidateQueries(QueryKeys.DYNAMIC_CONTENT_CATEGORIES),
                    queryClient.invalidateQueries(['manage-display-conditions']),
                ]);
                onSuccessOption?.(data, variables, context);
            },
            ...restMutateOptions,
        },
        ApiMethods.PUT
    );
};

export default useUpdateDisplayCondition;
